"use client";

import { c as _c } from "react/compiler-runtime";
import { approvalsInfo } from "@/info/approvals";
import formikOptions from "@/utils/formikOptions";
import { deepEqual as isEqualFunc } from "fast-equals";
import { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { DocumentsDetailsSection } from "../documents-form";
import { AmenitiesIconGrid } from "../rhf/amenities-icon-grid";
import { Input } from "../rhf/input";
import { ButtonActions } from "./action-buttons";
import { useFormContext } from "./context/FormProvider";
const waterSourceOptions = [{
  value: "borewell",
  label: "Borewell"
}, {
  value: "cauvery",
  label: "Cauvery"
}, {
  value: "water-tank",
  label: "Water tank"
}];
interface StepProjectOrCommunity {
  approachRoadWidth?: number;
  numEntrances?: number;
  totalFloors?: number;
  totalBasements?: number;
  waterSource?: string[];
  amenities?: string[];
  approvals?: string[];
}
interface StepProjectOrCommunityProps {
  onNext: (data: StepProjectOrCommunity) => void;
  onBack: () => void;
  isLoading?: boolean;
}
interface ProfileFormData {
  propertyType: {
    type: string;
    subType: string;
    houseType?: string;
  };
  isCommunity?: boolean;
}
export function StepProjectOrCommunity(t0) {
  const $ = _c(60);
  const {
    onNext,
    onBack,
    isLoading
  } = t0;
  const {
    state,
    updateStepData
  } = useFormContext();
  const contextProfile = state.profile as Partial<ProfileFormData>;
  const contextAdditional = state.other as Partial<StepProjectOrCommunity>;
  let t1;
  if ($[0] !== contextAdditional) {
    t1 = {
      ...contextAdditional
    };
    $[0] = contextAdditional;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const defaultValues = t1;
  const currentPropertyType = contextProfile.propertyType?.type || "";
  const isCommunity = contextProfile?.isCommunity || false;
  let t2;
  if ($[2] !== defaultValues) {
    t2 = {
      defaultValues,
      mode: "onChange"
    };
    $[2] = defaultValues;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const form = useForm(t2);
  let control;
  let formState;
  let handleSubmit;
  let reset;
  let setValue;
  let useFormProps;
  let watch;
  if ($[4] !== form) {
    ({
      control,
      handleSubmit,
      setValue,
      watch,
      formState,
      reset,
      ...useFormProps
    } = form);
    $[4] = form;
    $[5] = control;
    $[6] = formState;
    $[7] = handleSubmit;
    $[8] = reset;
    $[9] = setValue;
    $[10] = useFormProps;
    $[11] = watch;
  } else {
    control = $[5];
    formState = $[6];
    handleSubmit = $[7];
    reset = $[8];
    setValue = $[9];
    useFormProps = $[10];
    watch = $[11];
  }
  let t3;
  if ($[12] !== control) {
    t3 = {
      control
    };
    $[12] = control;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const watchedValues = useWatch(t3) as StepProjectOrCommunity;
  let t4;
  if ($[14] !== watchedValues) {
    t4 = watchedValues || {};
    $[14] = watchedValues;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const memoizedWatchedValues = t4;
  const prevWatched = useRef(memoizedWatchedValues);
  const updateTimeoutRef = useRef(null);
  let t5;
  let t6;
  if ($[16] !== memoizedWatchedValues || $[17] !== updateStepData) {
    t5 = () => {
      if (!isEqualFunc(prevWatched.current, memoizedWatchedValues)) {
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }
        updateTimeoutRef.current = setTimeout(() => {
          updateStepData("other", memoizedWatchedValues);
          prevWatched.current = memoizedWatchedValues;
        }, 300);
      }
      return () => {
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }
      };
    };
    t6 = [memoizedWatchedValues, updateStepData];
    $[16] = memoizedWatchedValues;
    $[17] = updateStepData;
    $[18] = t5;
    $[19] = t6;
  } else {
    t5 = $[18];
    t6 = $[19];
  }
  useEffect(t5, t6);
  const isApartment = currentPropertyType === "apartment";
  const isHouse = currentPropertyType === "house";
  const isVilla = currentPropertyType === "villa";
  let t7;
  if ($[20] !== onNext) {
    t7 = data => {
      onNext(data);
    };
    $[20] = onNext;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const onSubmit = t7;
  let t8;
  if ($[22] !== control || $[23] !== formState || $[24] !== handleSubmit || $[25] !== reset || $[26] !== setValue || $[27] !== useFormProps || $[28] !== watch) {
    t8 = {
      control,
      handleSubmit,
      setValue,
      watch,
      formState,
      reset,
      ...useFormProps
    };
    $[22] = control;
    $[23] = formState;
    $[24] = handleSubmit;
    $[25] = reset;
    $[26] = setValue;
    $[27] = useFormProps;
    $[28] = watch;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== handleSubmit || $[31] !== onSubmit) {
    t9 = handleSubmit(onSubmit);
    $[30] = handleSubmit;
    $[31] = onSubmit;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      required: "Approach road width is required",
      min: {
        value: 10,
        message: "Minimum width is 10 ft"
      }
    };
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  let t12;
  if ($[34] !== control) {
    t11 = <div className="grid grid-cols-1 md:grid-cols-2 gap-4"><Input label="Approach Road Width (ft) *" name="approachRoadWidth" type="number" min={10} step={10} required={true} control={control} rules={t10} /><Input label="Number of Entrances" name="numEntrances" type="number" control={control} /><Input label="Total No. of Floors" name="totalFloors" type="number" control={control} /><Input label="Total No. of Basements" name="totalBasements" type="number" control={control} /></div>;
    t12 = <Input type="checkbox" label="Water Source" name="waterSource" itemsClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2" options={waterSourceOptions} control={control} />;
    $[34] = control;
    $[35] = t11;
    $[36] = t12;
  } else {
    t11 = $[35];
    t12 = $[36];
  }
  let t13;
  if ($[37] !== control || $[38] !== isApartment || $[39] !== isCommunity || $[40] !== isHouse || $[41] !== isVilla) {
    t13 = Boolean(isApartment || isCommunity && isHouse || isVilla) && <div className="my-6"><AmenitiesIconGrid name="amenities" control={control} searchMode="fuzzy" allowCustom={true} maxShown={8} selectionMode="multiple" onCustomCreate={_temp} /></div>;
    $[37] = control;
    $[38] = isApartment;
    $[39] = isCommunity;
    $[40] = isHouse;
    $[41] = isVilla;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = formikOptions(approvalsInfo.bengaluru);
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== control) {
    t15 = <Input label="Approvals" name="approvals" type="checkbox" itemsClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2" options={t14} control={control} />;
    $[44] = control;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <DocumentsDetailsSection name="documents" />;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== isLoading || $[48] !== onBack) {
    t17 = <ButtonActions next={true} onBack={onBack} isLoading={isLoading} />;
    $[47] = isLoading;
    $[48] = onBack;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== t11 || $[51] !== t12 || $[52] !== t13 || $[53] !== t15 || $[54] !== t17 || $[55] !== t9) {
    t18 = <form onSubmit={t9} className="space-y-6 mx-auto p-2">{t11}{t12}{t13}{t15}{t16}{t17}</form>;
    $[50] = t11;
    $[51] = t12;
    $[52] = t13;
    $[53] = t15;
    $[54] = t17;
    $[55] = t9;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  let t19;
  if ($[57] !== t18 || $[58] !== t8) {
    t19 = <FormProvider {...t8}>{t18}</FormProvider>;
    $[57] = t18;
    $[58] = t8;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  return t19;
}
function _temp(name) {
  return {
    code: name,
    name,
    isCustom: true,
    icon: "star",
    order: 999,
    importance: 1,
    type: ["custom"]
  };
}